/* eslint-disable jsx-a11y/alt-text */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-25 12:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import classnames from 'classnames';
import { Icon } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import DeleteIcon from 'src/assets/images/theme-setting/delete.svg?react';
import noProductImage from 'src/assets/images/product/no-image.png';
import { MIN_FONT_SIZE, MAX_FONT_SIZE } from 'src/constants/theme-settings';
import { formatMoney } from 'src/helpers/money-format';

type Props = {
  formValues: any,
  previewProductData: any,
  device: number
};

type ComposedProps = Props & WithI18nProps;

class AjaxCartPreview extends Component<ComposedProps> {
  state = {
    onHoverCheckoutBtn: false,
    onHoverViewCartBtn: false
  };

  toggleHoverCheckoutBtn = () => {
    this.setState({ onHoverCheckoutBtn: !this.state.onHoverCheckoutBtn });
  };

  toggleHoverViewCartBtn = () => {
    this.setState({ onHoverViewCartBtn: !this.state.onHoverViewCartBtn });
  };

  getProductTitle = product => {
    if (_.isEmpty(product)) return '';
    const { split_product = false } = product;
    if (split_product) return product.original_title || product.title;
    return product.title;
  };

  getValidFontSize = fontSize => {
    if (!fontSize || fontSize < MIN_FONT_SIZE || fontSize > MAX_FONT_SIZE) return 14;

    return fontSize;
  };

  getDefaultLabels = () => {
    const { labelTranslation } = this.props;
    const defaultValues = _(labelTranslation)
      .pick([
        'atcMiniCartViewCartLabelV2',
        'atcMiniCartCheckoutLabelV2',
        'atcMiniCartShopingCartLabelV2',
        'atcMiniCartSubtotalLabelV2',
        'atcMiniCartCountItemLabelV2',
        'atcMiniCartCountItemLabelPluralV2'
      ])
      .reduce(
        (acc, { key, value }) => {
          if (_.isEmpty(value)) return acc;
          acc[key] = value;
          return acc;
        },
        {
          atcMiniCartViewCartLabelV2: 'View cart',
          atcMiniCartCheckoutLabelV2: 'Checkout',
          atcMiniCartShopingCartLabelV2: 'Your cart',
          atcMiniCartSubtotalLabelV2: 'Subtotal',
          atcMiniCartCountItemLabelV2: 'item',
          atcMiniCartCountItemLabelPluralV2: 'items'
        }
      );

    return defaultValues;
  };

  render() {
    const { formValues, previewProductData = {}, isExpandFullscreen } = this.props;
    const { shop = {}, products: previewProducts = [] } = previewProductData;
    const { cart_setting = {} } = formValues;
    const defaultLabels = this.getDefaultLabels();
    const cartStyle = cart_setting?.cartStyle;
    const enableCart = cart_setting?.enableCart && ['popup', 'side'].includes(cartStyle);

    const fontStyle = cart_setting?.generalLayout?.fontStyle;
    const fontStyleCss = fontStyle?.split('-')[0] || 'normal';
    const fontWeightCss = fontStyle?.split('-')[1] || '400';

    const subTotal = _.sumBy(previewProducts, 'price_min');
    const itemsOnCart = _.size(previewProducts);
    const buttonShape = cart_setting?.generalLayout?.shape;
    const fontSize = this.getValidFontSize(cart_setting?.generalLayout?.fontSize);
    const viewCartText =
      cart_setting?.viewCartBtn?.text || defaultLabels['atcMiniCartViewCartLabelV2'];
    const viewCartTextColor = cart_setting?.viewCartBtn?.color;
    const viewCartTextHoverColor = cart_setting?.viewCartBtn?.hoverColor;
    const viewCartBackgroundColor = cart_setting?.viewCartBtn?.backgroundColor;
    const viewCartHoverBackgroundColor = cart_setting?.viewCartBtn?.hoverBackgroundColor;
    const viewCartBorderColor = cart_setting?.viewCartBtn?.borderColor;
    const viewCartHoverBorderColor = cart_setting?.viewCartBtn?.hoverBorderColor;
    const viewCartTextTransform = cart_setting?.viewCartBtn?.textTransform;
    const checkoutText =
      cart_setting?.checkoutBtn?.text || defaultLabels['atcMiniCartCheckoutLabelV2'];
    const checkoutTextColor = cart_setting?.checkoutBtn?.color;
    const checkoutTextHoverColor = cart_setting?.checkoutBtn?.hoverColor;
    const checkoutBackgroundColor = cart_setting?.checkoutBtn?.backgroundColor;
    const checkoutHoverBackgroundColor = cart_setting?.checkoutBtn?.hoverBackgroundColor;
    const checkoutBorderColor = cart_setting?.checkoutBtn?.borderColor;
    const checkoutHoverBorderColor = cart_setting?.checkoutBtn?.hoverBorderColor;
    const checkoutTextTransform = cart_setting?.checkoutBtn?.textTransform;

    const btnClasses = classnames('button-item', {
      'button-item--rounded': buttonShape === 'round'
    });

    return (
      <div className={`visual-editor-preview__overlay ${cartStyle === 'side' ? 'no-padding' : ''}`}>
        {enableCart && (
          <div className={`ajax-cart ajax-cart--${cartStyle} ${!isExpandFullscreen ? '' : 'full'}`}>
            <div className="ajax-cart__header">
              <div>
                {defaultLabels['atcMiniCartShopingCartLabelV2']} (
                {`${itemsOnCart} ${
                  itemsOnCart === 1
                    ? defaultLabels['atcMiniCartCountItemLabelV2']
                    : defaultLabels['atcMiniCartCountItemLabelPluralV2']
                }`}
                )
              </div>
              <div className="close-popup-btn">
                <Icon source={CancelSmallMinor} color="base" />
              </div>
            </div>
            <div className="ajax-cart__cart">
              {!_.isEmpty(previewProducts) &&
                previewProducts.map((product, index) => {
                  const { images_info = [], options_with_values = [] } = product;
                  const mainImage = _.head(images_info);

                  const isDefaultOption =
                    options_with_values?.length === 1 &&
                    options_with_values?.[0]?.values?.[0]?.title === 'Default Title';

                  return (
                    <div key={index} className="product-item">
                      <div className="product-item__media">
                        <img
                          className="product-item__single-photo"
                          src={mainImage?.src || noProductImage}
                          alt={mainImage?.alt}
                        />
                      </div>
                      <div className="product-item__information-wrapper">
                        <div className="product-item__name">
                          <p>{this.getProductTitle(product)}</p>
                          <div className="product-item__delete-btn">
                            <span className="Polaris-Icon">
                              <DeleteIcon className="Polaris-Icon__Svg" />
                            </span>
                          </div>
                        </div>
                        <div className="product-item__information-inner">
                          <div className="product-item__information">
                            <div className="product-item__options">
                              {!isDefaultOption &&
                                !_.isEmpty(options_with_values) &&
                                options_with_values.map((option, index) => {
                                  const { label = 'label', values = [] } = option;
                                  const firstValue = _.head(values);

                                  return (
                                    <div key={index} className="product-option">
                                      <div className="product-option__label">{label}:</div>
                                      <div className="product-option__value">
                                        {firstValue?.title}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                          <div className="product-item__first-price">
                            {formatMoney(product?.price_min, shop?.moneyFormat)}
                          </div>
                          <div className="product-item__quantity-counter">
                            <div className="quantity-counter__counter">
                              <div>-</div>
                              <div>1</div>
                              <div>+</div>
                            </div>
                            <div className="product-item__third-price">
                              <div>X</div>
                              <div>{formatMoney(product?.price_min, shop?.moneyFormat)}</div>
                            </div>
                          </div>
                          <div className="product-item__second-price">
                            {formatMoney(product?.price_min, shop?.moneyFormat)}
                          </div>
                          {/* <div className="close-popup-btn">
                            <DeleteIcon />
                          </div> */}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="ajax-cart__footer">
              <div className="ajax-cart__sub-total">
                <div className="text">{defaultLabels['atcMiniCartSubtotalLabelV2']}:</div>
                <div className="price">{formatMoney(subTotal, shop?.moneyFormatbTotal)}</div>
              </div>
              <div className="ajax-cart__buttons">
                <div
                  className={btnClasses}
                  style={{
                    textTransform: checkoutTextTransform,
                    fontSize,
                    color: this.state.onHoverCheckoutBtn
                      ? checkoutTextHoverColor
                      : checkoutTextColor,
                    backgroundColor: this.state.onHoverCheckoutBtn
                      ? checkoutHoverBackgroundColor
                      : checkoutBackgroundColor,
                    borderColor: this.state.onHoverCheckoutBtn
                      ? checkoutHoverBorderColor
                      : checkoutBorderColor,
                    fontStyle: fontStyleCss,
                    fontWeight: fontWeightCss
                  }}
                  onMouseEnter={this.toggleHoverCheckoutBtn}
                  onMouseLeave={this.toggleHoverCheckoutBtn}>
                  {checkoutText}
                </div>
                <div
                  className={btnClasses}
                  style={{
                    textTransform: viewCartTextTransform,
                    fontSize,
                    color: this.state.onHoverViewCartBtn
                      ? viewCartTextHoverColor
                      : viewCartTextColor,
                    backgroundColor: this.state.onHoverViewCartBtn
                      ? viewCartHoverBackgroundColor
                      : viewCartBackgroundColor,
                    borderColor: this.state.onHoverViewCartBtn
                      ? viewCartHoverBorderColor
                      : viewCartBorderColor,
                    fontStyle: fontStyleCss,
                    fontWeight: fontWeightCss
                  }}
                  onMouseEnter={this.toggleHoverViewCartBtn}
                  onMouseLeave={this.toggleHoverViewCartBtn}>
                  {viewCartText}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withI18n()(AjaxCartPreview);
